/**
 * The page's idea of "now", in unix milliseconds, in the same timebase as the wire's timestamps.
 *
 * On the live site that is `Date.now()` and nothing else. Under `?demo=` it is the simulator's own
 * clock (`DemoServer.now`), which starts at `DEMO_BASE_NOW` and advances only when the simulator
 * steps — so under `?freeze=1&t=3.6` it stands still at exactly 3.6 s into the scenario, on every
 * machine, forever.
 *
 * It exists for the wall-fall moments (F28). A moment is a *transition* with a duration, and a
 * frozen screenshot at `t` has to show the frame the moment is on at `t`: the stamp landed, the
 * horde halfway through the rubble. `?t=` replays the scenario before React mounts, so whoever
 * draws the moment mounts *after* the fall and has to work out how far in it is — and the only
 * clock that answers the same on a laptop and on a CI runner is the simulator's. `Date.now()` would
 * make the answer depend on how long Pixi took to initialise, and the moment's `at` was recorded
 * from this clock too, so live and demo agree on the arithmetic and differ only in the source.
 *
 * Deliberately tiny and dependency-free: the store records `at` from it and the `/v2` chunk reads
 * ages from it, and neither side may import the demo chunk or the renderer to do so.
 */

type Source = () => number

let source: Source = () => Date.now()

export function pageNow(): number {
  return source()
}

/**
 * Install another source of time, or `null` to go back to the wall clock. The demo transport is
 * the only production caller; it does this before React mounts, alongside the scene clock.
 */
export function setPageClock(next: Source | null): void {
  source = next ?? (() => Date.now())
}

/** Test seam. */
export function resetPageClock(): void {
  source = () => Date.now()
}
